import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useEffect } from 'react';

// Импорт адаптивных стилей
import './styles/responsive.css';

// Лёгкие обёртки и компоненты первого рендера остаются статическими.
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import GuestRoute from './components/GuestRoute';
import AppProtectedRoute from './components/AppProtectedRoute';
import AppUpdateBanner from './components/AppUpdateBanner';
import AppRecommendationModal from './components/AppRecommendationModal';
import CookieConsentBanner from './components/CookieConsentBanner';

// Тяжёлые страницы грузятся лениво (React.lazy + dynamic import),
// чтобы код каждой страницы попадал в отдельный чанк и не раздувал main.js.
const LoginPage = lazy(() => import('./pages/AuthLoginPage'));
const SsoCallbackPage = lazy(() => import('./pages/AuthSsoCallbackPage'));
const RegisterPage = lazy(() => import('./pages/AuthRegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TranscriptionPage = lazy(() => import('./pages/TranscriptionPage'));
const SharedTranscriptionPage = lazy(() => import('./pages/SharedTranscriptionPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AIHistoryPage = lazy(() => import('./pages/AIHistoryPage'));
const UnderDevelopmentPage = lazy(() => import('./pages/UnderDevelopmentPage'));
const SmartizerPage = lazy(() => import('./pages/SmartizerPage'));
const HRAgentPage = lazy(() => import('./pages/HRAgentPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const RagPage = lazy(() => import('./pages/RagPage'));
const RagV2SettingsPage = lazy(() => import('./pages/RagV2SettingsPage'));
// RiskAnalysisPage перенесен в AI Юрист
const ContentFactoryPage = lazy(() => import('./pages/ContentFactoryPage'));
const LegalAssistantPage = lazy(() => import('./pages/LegalAssistantPage'));
const LegalAssistantV2Page = lazy(() => import('./pages/LegalAssistantV2Page'));
const DeepLogPage = lazy(() => import('./pages/DeepLogPage'));
const TechWriterPage = lazy(() => import('./pages/TechWriterPage'));
const AIPresentationPage = lazy(() => import('./pages/AIPresentationPage'));
const AIPolygonPage = lazy(() => import('./pages/AIPolygonPage'));
const AITrainerPage = lazy(() => import('./pages/AITrainerPage'));
const WebAnalystPage = lazy(() => import('./pages/WebAnalystPage'));
const WebAnalystHelpPage = lazy(() => import('./pages/WebAnalystHelpPage'));
const APIDocsPage = lazy(() => import('./pages/APIDocsPage'));
const OCRScannerPage = lazy(() => import('./pages/OCRScannerPage'));
const AIChatPage = lazy(() => import('./pages/AIChatPage'));
const ResetPasswordPage = lazy(() => import('./pages/AuthResetPasswordPage'));
const EmailVerificationPage = lazy(() => import('./pages/AuthEmailVerificationPage'));
const CompanyJoinPage = lazy(() => import('./pages/CompanyJoinPage'));
const BlockedDomainsPage = lazy(() => import('./pages/BlockedDomainsPage'));
const TokenPricingPage = lazy(() => import('./pages/TokenPricingPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const AdminSupportPage = lazy(() => import('./pages/AdminSupportPage'));
const AdminSupportTicketPage = lazy(() => import('./pages/AdminSupportTicketPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentFailedPage = lazy(() => import('./pages/PaymentFailedPage'));
const SynkaMessengerPage = lazy(() => import('./pages/SynkaMessengerPage'));
const SynkaMessengerInfoPage = lazy(() => import('./pages/SynkaMessengerInfoPage'));
const SynkaAttachmentViewerPage = lazy(() => import('./pages/SynkaAttachmentViewerPage'));
const BISystemPage = lazy(() => import('./pages/BISystemPage'));
const VidzheyPage = lazy(() => import('./pages/VidzheyPage'));
const VidzheyPublicJoinPage = lazy(() => import('./pages/VidzheyPublicJoinPage'));
const SmartLinksPage = lazy(() => import('./pages/SmartLinksPage'));
const LegalAuditPage = lazy(() => import('./pages/LegalAuditPage'));
const SiteAnalyzerPage = lazy(() => import('./pages/SiteAnalyzerPage'));
const OsvAnalysisPage = lazy(() => import('./pages/OsvAnalysisPage'));
const ZaoModulePage = lazy(() => import('./pages/ZaoModulePage'));
const DiskPage = lazy(() => import('./pages/DiskPage'));
const DiskSharePage = lazy(() => import('./pages/DiskSharePage'));
const AppBuilderListPage = lazy(() => import('./pages/AppBuilderListPage'));
const AppBuilderStudioPage = lazy(() => import('./pages/AppBuilderStudioPage'));
const AppBuilderRunPage = lazy(() => import('./pages/AppBuilderRunPage'));

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  
  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

// Компонент для редиректа на внешний URL
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  
  return null;
}

function ShortLinkRedirect() {
  const { shortCode } = useParams();

  useEffect(() => {
    if (shortCode) {
      const params = new URLSearchParams(window.location.search);
      if (document.referrer) {
        params.set('source_referrer', document.referrer);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      window.location.replace(`/api/s/${encodeURIComponent(shortCode)}${query}`);
    }
  }, [shortCode]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
      Переходим по короткой ссылке...
    </div>
  );
}

function App() {
  const location = useLocation();
  
  // Страницы где не нужно показывать Header (используют собственную навигацию..)
  const hideHeaderPaths = [
    '/dashboard',
    '/transcription',
    '/shared/transcription',
    '/smartizer',
    '/hr-agent',
    '/content-factory',
    '/legal-assistant-v2',
    '/rag-v2',
    '/deeplog',
    '/tech-writer',
    '/ai-presentation',
    '/ai-polygon',
    '/ai-trainer',
    '/web-analyst',
    '/web-analyst/help',
    '/bi-system',
    '/vidzhey',
    '/smart-links',
    '/site-analyzer',
    '/osv-analysis',
    '/ocr-scanner',
    '/ai-history',
    '/knowledge-base',
    '/billing',
    '/profile',
    '/api-docs',
    '/admin',
    '/ai-chat',
    '/reset-password',
    '/verify-email',
    '/login',
    '/register',
    '/s',
    '/company/join',
    '/blocked-domains',
    '/support',
    '/support/tickets',
    '/admin/support',
    '/admin/support/tickets',
    '/cookie',
    '/app-builder',
    '/polozhenie-ob-obrabotke',
    '/soglasie-na-obrabotku',
    '/user_agreement',
    '/oferta',
    '/tarif',
    '/pricing',
    '/billing/payment-success',
    '/billing/payment-failed',
    '/synka-messenger',
    '/token_pricing',
    '/disk'
  ];
  const isAuthPage = ['/', '/login', '/register', '/reset-password', '/verify-email']
    .includes(location.pathname);
  const shouldHideHeader = isAuthPage || hideHeaderPaths.some(path => location.pathname.startsWith(path));
  
  // Страницы где не нужно показывать Footer.
  const hideFooterPaths = [
    '/rag-v2',
    '/rag-v2/settings',
    '/ai-chat',
    '/bi-system',
    '/knowledge-base',
    '/vidzhey',
    '/smart-links',
    '/site-analyzer',
    '/osv-analysis',
    '/s',
    '/shared/transcription',
    '/synka-messenger',
    '/token_pricing'
  ];
  const shouldHideFooter = hideFooterPaths.includes(location.pathname)
    || location.pathname.startsWith('/synka-messenger/')
    || location.pathname.startsWith('/vidzhey/')
    || location.pathname.startsWith('/smart-links/')
    || location.pathname.startsWith('/site-analyzer/')
    || location.pathname.startsWith('/osv-analysis/')
    || location.pathname.startsWith('/s/')
    || location.pathname.startsWith('/knowledge-base/')
    || location.pathname.startsWith('/shared/transcription/')
    || location.pathname.startsWith('/disk');

  const isTokenPricingPage = location.pathname === '/token_pricing';

  return (
    <AppContainer>
      {!isTokenPricingPage && <AppUpdateBanner />}
      {!shouldHideHeader && <Header />}
      <AppRecommendationModal />
      <MainContent>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          }
        >
        <Routes>
          <Route path="/" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/login/sso-callback" element={<SsoCallbackPage />} />
          <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/s/:shortCode" element={<ShortLinkRedirect />} />
          <Route path="/company/join" element={<CompanyJoinPage />} />
          <Route path="/blocked-domains" element={<BlockedDomainsPage />} />
          <Route path="/token_pricing" element={<TokenPricingPage />} />
          
          {/* Защищенные роуты */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/transcription" element={
            <AppProtectedRoute appId="transcriber">
              <TranscriptionPage />
            </AppProtectedRoute>
          } />
          <Route path="/shared/transcription/:token" element={<SharedTranscriptionPage />} />
          <Route path="/profile" element={
            <ProtectedRoute serviceKey="profile">
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/ai-history" element={
            <ProtectedRoute>
              <AIHistoryPage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/support" element={
            <ProtectedRoute adminOnly requiredPermission="tickets">
              <AdminSupportPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/support/tickets/:id" element={
            <ProtectedRoute adminOnly requiredPermission="tickets">
              <AdminSupportTicketPage />
            </ProtectedRoute>
          } />
          <Route path="/under-development" element={
            <ProtectedRoute>
              <UnderDevelopmentPage />
            </ProtectedRoute>
          } />
          <Route path="/smartizer" element={
            <AppProtectedRoute appId="smartizer">
              <SmartizerPage />
            </AppProtectedRoute>
          } />
          <Route path="/hr-agent" element={
            <AppProtectedRoute appId="hr_agent">
              <HRAgentPage />
            </AppProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute serviceKey="billing">
              <BillingPage />
            </ProtectedRoute>
          } />
          <Route path="/rag" element={
            <ProtectedRoute>
              <RagPage />
            </ProtectedRoute>
          } />
          <Route path="/rag-v2" element={
            <AppProtectedRoute appId="rag_v2">
              <RagV2SettingsPage />
            </AppProtectedRoute>
          } />
          <Route path="/rag-v2/settings" element={
            <AppProtectedRoute appId="rag_v2">
              <RagV2SettingsPage />
            </AppProtectedRoute>
          } />
          {/* RiskAnalysisPage перенесен в AI Юрист как тип документа */}
          <Route path="/content-factory" element={
            <AppProtectedRoute appId="content_factory">
              <ContentFactoryPage />
            </AppProtectedRoute>
          } />
          <Route path="/legal-assistant" element={
            <ProtectedRoute>
              <LegalAssistantPage />
            </ProtectedRoute>
          } />
          <Route path="/legal-assistant-v2" element={
            <AppProtectedRoute appId="legal_assistant_v2">
              <LegalAssistantV2Page />
            </AppProtectedRoute>
          } />
          <Route path="/deeplog" element={
            <AppProtectedRoute appId="deeplog">
              <DeepLogPage />
            </AppProtectedRoute>
          } />
          <Route path="/web-analyst" element={
            <AppProtectedRoute appId="web_analyst">
              <WebAnalystPage />
            </AppProtectedRoute>
          } />
          <Route path="/web-analyst/help" element={
            <AppProtectedRoute appId="web_analyst">
              <WebAnalystHelpPage />
            </AppProtectedRoute>
          } />
          <Route path="/ocr-scanner" element={
            <AppProtectedRoute appId="ocr_scanner">
              <OCRScannerPage />
            </AppProtectedRoute>
          } />
          <Route path="/api-docs" element={
            <ProtectedRoute serviceKey="api_docs">
              <APIDocsPage />
            </ProtectedRoute>
          } />
          <Route path="/ai-chat" element={
            <AppProtectedRoute appId="ai_chat">
              <AIChatPage />
            </AppProtectedRoute>
          } />
          <Route path="/tech-writer" element={
            <AppProtectedRoute appId="tech_writer">
              <TechWriterPage />
            </AppProtectedRoute>
          } />
          <Route path="/ai-presentation" element={
            <AppProtectedRoute appId="ai_presentation">
              <AIPresentationPage />
            </AppProtectedRoute>
          } />
          <Route path="/ai-polygon" element={
            <AppProtectedRoute appId="ai_polygon">
              <AIPolygonPage />
            </AppProtectedRoute>
          } />
          <Route path="/ai-polygon/:diagramId" element={
            <AppProtectedRoute appId="ai_polygon">
              <AIPolygonPage />
            </AppProtectedRoute>
          } />
          <Route path="/ai-trainer" element={
            <AppProtectedRoute appId="ai_trainer">
              <AITrainerPage />
            </AppProtectedRoute>
          } />
          <Route path="/bi-system" element={
            <AppProtectedRoute appId="bi_system">
              <BISystemPage />
            </AppProtectedRoute>
          } />
          <Route path="/vidzhey" element={
            <AppProtectedRoute appId="vidzhey">
              <VidzheyPage />
            </AppProtectedRoute>
          } />
          <Route path="/vidzhey/archive" element={
            <AppProtectedRoute appId="vidzhey">
              <VidzheyPage mode="archive" />
            </AppProtectedRoute>
          } />
          <Route path="/vidzhey/details/:roomId" element={
            <AppProtectedRoute appId="vidzhey">
              <VidzheyPage mode="details" />
            </AppProtectedRoute>
          } />
          <Route path="/vidzhey/room/:roomId" element={
            <AppProtectedRoute appId="vidzhey">
              <VidzheyPage mode="room" />
            </AppProtectedRoute>
          } />
          <Route path="/vidzhey/join/:slug" element={<VidzheyPublicJoinPage />} />
          <Route path="/smart-links" element={
            <AppProtectedRoute appId="smart_links">
              <SmartLinksPage />
            </AppProtectedRoute>
          } />
          <Route path="/smart-links/:linkId" element={
            <AppProtectedRoute appId="smart_links">
              <SmartLinksPage />
            </AppProtectedRoute>
          } />
          <Route path="/smart-links/legal-audit" element={
            <AppProtectedRoute appId="smart_links">
              <LegalAuditPage />
            </AppProtectedRoute>
          } />
          <Route path="/site-analyzer" element={
            <AppProtectedRoute appId="site_analyzer">
              <SiteAnalyzerPage />
            </AppProtectedRoute>
          } />
          <Route path="/site-analyzer/:taskId" element={
            <AppProtectedRoute appId="site_analyzer">
              <SiteAnalyzerPage />
            </AppProtectedRoute>
          } />
          <Route path="/osv-analysis" element={
            <AppProtectedRoute appId="osv_analysis">
              <OsvAnalysisPage />
            </AppProtectedRoute>
          } />
          <Route path="/osv-analysis/:jobId" element={
            <AppProtectedRoute appId="osv_analysis">
              <OsvAnalysisPage />
            </AppProtectedRoute>
          } />
          <Route path="/zao-module" element={
            <AppProtectedRoute appId="zao_module">
              <ZaoModulePage />
            </AppProtectedRoute>
          } />
          <Route path="/zao-module/:reportId" element={
            <AppProtectedRoute appId="zao_module">
              <ZaoModulePage />
            </AppProtectedRoute>
          } />
          <Route path="/disk" element={
            <AppProtectedRoute appId="disk">
              <DiskPage />
            </AppProtectedRoute>
          } />
          <Route path="/disk/s/:token" element={<DiskSharePage />} />
          <Route path="/support" element={
            <ProtectedRoute serviceKey="support">
              <SupportPage />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-base" element={
            <ProtectedRoute serviceKey="knowledge_base">
              <KnowledgeBasePage />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-base/:slug" element={
            <ProtectedRoute serviceKey="knowledge_base">
              <KnowledgeBasePage />
            </ProtectedRoute>
          } />
          <Route path="/app-builder" element={
            <ProtectedRoute>
              <AppBuilderListPage />
            </ProtectedRoute>
          } />
          <Route path="/app-builder/:appId/edit" element={
            <ProtectedRoute>
              <AppBuilderStudioPage />
            </ProtectedRoute>
          } />
          <Route path="/app-builder/:appId/run" element={
            <ProtectedRoute>
              <AppBuilderRunPage />
            </ProtectedRoute>
          } />
          <Route path="/support/tickets/:id" element={
            <ProtectedRoute serviceKey="support">
              <SupportTicketPage />
            </ProtectedRoute>
          } />
          <Route path="/pricing" element={
            <ProtectedRoute serviceKey="pricing">
              <PricingPage />
            </ProtectedRoute>
          } />
          
          {/* Страницы результата оплаты */}
          <Route path="/billing/payment-success" element={
            <ProtectedRoute serviceKey="billing">
              <PaymentSuccessPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/payment-failed" element={
            <ProtectedRoute serviceKey="billing">
              <PaymentFailedPage />
            </ProtectedRoute>
          } />
          <Route path="/synka-messenger" element={
            <AppProtectedRoute appId="synka_messenger">
              <SynkaMessengerInfoPage />
            </AppProtectedRoute>
          } />
          <Route path="/synka-messenger/web" element={
            <AppProtectedRoute appId="synka_messenger">
              <SynkaMessengerPage />
            </AppProtectedRoute>
          } />
          <Route path="/synka-messenger/view" element={
            <AppProtectedRoute appId="synka_messenger">
              <SynkaAttachmentViewerPage />
            </AppProtectedRoute>
          } />
          
          {/* Редиректы на внешние URL */}
          <Route path="/cookie" element={<ExternalRedirect to="https://onlanta.ai/cookie" />} />
          <Route path="/polozhenie-ob-obrabotke" element={<ExternalRedirect to="https://onlanta.ai/polozhenie-ob-obrabotke" />} />
          <Route path="/soglasie-na-obrabotku" element={<ExternalRedirect to="https://onlanta.ai/soglasie-na-obrabotku" />} />
          <Route path="/user_agreement" element={<ExternalRedirect to="https://onlanta.ai/user_agreement" />} />
          <Route path="/oferta" element={<ExternalRedirect to="https://onlanta.ai/oferta" />} />
          <Route path="/tarif" element={<ExternalRedirect to="https://onlanta.ai/tarif" />} />
        </Routes>
        </Suspense>
      </MainContent>
      {!shouldHideFooter && <Footer auth={isAuthPage} />}
      <CookieConsentBanner />
    </AppContainer>
  );
}

export default App;